import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import AuthService from 'services/api/Auth';
import Storage from 'utils/Storage';
import handlePromise from 'utils/Promise';
import {
  clearSsoSessionKeys, getOrCreateSsoDeviceId, setRefreshToken,
} from 'utils/SsoSession';
import {
  extractSsoMessageCode, shouldResetSsoFlowByCode, translateSsoErrorCode,
} from 'utils/SsoError';
import { LoadingContext } from 'context/Loading';
import { updateUserToken } from 'store/User';
import { loadAndLogAppInfo } from 'utils/appInfo';
import i18next from 'i18next';
import useSessionBootstrap from 'hooks/useSessionBootstrap';
import LoginSsoView, { STEPS } from './components/View';

const HANDOFF_ERROR_KEYS: Record<string, string> = {
  invalid_origin: 'loginSso.errors.HANDOFF_INVALID_ORIGIN',
  not_configured: 'loginSso.errors.HANDOFF_NOT_CONFIGURED',
  handoff_missing_ticket: 'loginSso.errors.HANDOFF_MISSING_TICKET',
  handoff_failed: 'loginSso.errors.HANDOFF_FAILED',
  handoff_not_provisioned: 'loginSso.errors.HANDOFF_NOT_PROVISIONED',
};

/** El backend responde 503 cuando el SSO está caído (antes llegaba como 400 genérico). */
const isServiceUnavailable = ( response: { status?: number } | undefined, error: unknown ) => (
  response?.status === 503 || String( error ).includes( '503' )
);

function LoginSso() {
  const dispatch = useDispatch();
  const { toggleLoading } = useContext( LoadingContext );
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<STEPS>( STEPS.REQUEST_OTP );
  const [identifier, setIdentifier] = useState( '' );
  const [submitError, setSubmitError] = useState<string | undefined>();
  const { bootstrap, accountModal } = useSessionBootstrap();

  useEffect( () => {
    // Llegar aquí es empezar de cero: tampoco puede quedar un refresh vivo en el navegador
    // (p. ej. tras recargar, que en producción no conserva el usuario).
    Storage.clear( 'grupoimpar:token' );
    clearSsoSessionKeys();
    loadAndLogAppInfo();
  }, [] );

  useEffect( () => {
    const code = searchParams.get( 'error' );
    if ( !code ) return;
    const key = HANDOFF_ERROR_KEYS[code];
    if ( !key ) return;
    setSubmitError( i18next.t( key ) );
  }, [searchParams] );

  const resetSsoFlow = useCallback( () => {
    Storage.clear( 'grupoimpar:token' );
    clearSsoSessionKeys();
    setIdentifier( '' );
    setStep( STEPS.REQUEST_OTP );
  }, [] );

  const requestOtp = useCallback( async ( values: Record<string, any> ) => {
    toggleLoading( true );
    setSubmitError( undefined );
    const cleanIdentifier = String( values.identifier ?? '' ).trim().toLowerCase();
    const [error, response] = await handlePromise( AuthService.ssoLogin( {
      identifier: cleanIdentifier,
      deviceId: getOrCreateSsoDeviceId(),
      lang: 'es',
    } ) );
    toggleLoading( false );

    if ( !response.ok ) {
      const messageCode = extractSsoMessageCode( error );

      if ( isServiceUnavailable( response, error ) ) {
        setSubmitError( translateSsoErrorCode( 'SERVICE_UNAVAILABLE' ) );
        return;
      }

      if ( shouldResetSsoFlowByCode( messageCode ) ) resetSsoFlow();
      setSubmitError( translateSsoErrorCode( messageCode, 'REQUEST_OTP_FALLBACK' ) );
      return;
    }

    setIdentifier( cleanIdentifier );
    setStep( STEPS.VERIFY_OTP );
  }, [resetSsoFlow] );

  const verifyOtp = useCallback( async ( values: Record<string, any> ) => {
    toggleLoading( true );
    setSubmitError( undefined );
    const [error, response, responseData] = await handlePromise( AuthService.ssoConfirm( {
      identifier,
      deviceId: getOrCreateSsoDeviceId(),
      otpCode: String( values.otpCode ?? '' ).trim().toUpperCase(),
    } ) );
    toggleLoading( false );

    if ( !response.ok ) {
      const messageCode = extractSsoMessageCode( error );

      if ( isServiceUnavailable( response, error ) ) {
        setSubmitError( translateSsoErrorCode( 'SERVICE_UNAVAILABLE' ) );
        return;
      }

      if ( shouldResetSsoFlowByCode( messageCode ) ) resetSsoFlow();

      setSubmitError( translateSsoErrorCode( messageCode, 'VERIFY_OTP_FALLBACK' ) );
      return;
    }

    const accessToken = responseData?.accessToken;
    if ( !accessToken ) {
      setSubmitError( translateSsoErrorCode( 'MISSING_ACCESS_TOKEN' ) );
      return;
    }

    Storage.set( 'grupoimpar:token', accessToken );
    setRefreshToken( responseData?.refreshToken );
    dispatch( updateUserToken( accessToken ) );
    bootstrap();
  }, [identifier, resetSsoFlow] );

  return (
    <>
      <LoginSsoView
        step={step}
        identifier={identifier}
        submitError={submitError}
        requestOtp={requestOtp}
        verifyOtp={verifyOtp}
        onChangeEmail={() => {
          setSubmitError( undefined );
          setIdentifier( '' );
          setStep( STEPS.REQUEST_OTP );
        }}
      />

      {accountModal}
    </>
  );
}

export default LoginSso;
